'use strict';
const validation = require('./validation');
const {Answers} = require('./consts');
const {
    NO_ANSWER,
    NO_OPINION_ANSWER,
    OTHER_ANSWER,
    OTHER_ANSWER_WITH_TEXT
} = Answers;

/**
 * Добавляет новый узел в массив узлов, каждый из которых определяет условия показа соответствующей группы/вопроса.
 * @param {array} nodes - массив узлов
 * @param {object} nodeInfo - данные добавляемого узла
 * @param {string} expr - условие показа добавляемого узла
 */
function _append(nodes, nodeInfo, expr) {
    if (!expr) {
        return;
    }

    nodes.push(Object.assign({}, nodeInfo, {expr: expr}));
}

/**
 * Возвращает текущий ответ на вопрос.
 * @param {object} стейт вопроса
 * @return {string || array} для вопросов типа number, shortText и т.д вернет просто строку, для вопросов типа choice вернет массив
 */
function _getValue(question) {
    // Если это текстовый вопрос просто возвращаем значение
    if (question.value) {
        return question.value;
    }

    // Если это choice, то высчитываем значение
    if (question.noAnswer && question.noAnswer.checked) {
        return [NO_ANSWER];
    }
    if (question.noOpinionAnswer && question.noOpinionAnswer.checked) {
        return [NO_OPINION_ANSWER];
    }

    const options = question.options || [];
    let ids = options
        .filter(({checked}) => Boolean(checked))
        .map(({id}) => id);

    if (question.otherAnswer && question.otherAnswer.checked) {
        ids = ids.concat(OTHER_ANSWER);
    }

    if (question.otherAnswerWithText && question.otherAnswerWithText.checked) {
        ids = ids.concat(OTHER_ANSWER_WITH_TEXT);
    }

    if (ids.length) {
        return ids;
    }
    return null;
}

/**
 * Возвращает текст для из поля 'Другое с текстом'
 * @param {object} стейт вопроса
 * @return {string}
 */

function _getOtherValue(question) {
    const otherAnswerWithText = question.otherAnswerWithText || {};
    if (otherAnswerWithText.checked) {
        return otherAnswerWithText.value || null;
    }

    return null;
}

/**
 * Вычисляет условие показа для группы на основе условий показа вопросов вопросов, которые она содержит.
 * Если присутствуют вопросы, которые показываются всегда, то оставляем условие показа группы без изменений.
 * В противном случае объединяем условия показа всех вопросов с условием показа группы.
 * Если все вопросы скрыты, то скрывается и группа.
 * Дочерние группы игнорируются.
 * @param {object} group - группа
 * @param {object} questions - вопросы, которые необходимо обработать
 * @return {string} итоговое условие показа группы
 */
function _combineShowIf({group, questions, widgets}) {
    let groupShowIf = group.showIf;

    if (!group.children.length) {
        return groupShowIf;
    }

    const childrenShowIf = group.children.map(child => {
        const {id, type} = child;

        switch (type) {
            case 'question': {
                return questions[id] && questions[id].showIf;
            }
            case 'widget': {
                return widgets[id] && widgets[id].showIf;
            }
            default: {
                return null;
            }
        }
    }).filter(Boolean);

    // Если присутствуют вопросы, которые показываются всегда, то оставляем условие показа группы без изменений.
    if (childrenShowIf.length < group.children.length) {
        return groupShowIf;
    }

    // Объединяем все выражения вопросов через блок or. Если Выполняется условие для показа хотя бы одного вопроса,
    // значит надо показать страницу, с учетом, что выполнится условие показа самой страницы
    const blockChildrenShowIf = childrenShowIf.reduce((groupShowIf, questionShowIf) => {
        groupShowIf.content.push(questionShowIf);
        return groupShowIf;
    }, {type: 'block', op: 'or', content: []});

    // Если есть условие показа страницы, то объединяем его с условием показа вопросов
    if (groupShowIf) {
        return {type: 'block', op: 'and', content: [groupShowIf, blockChildrenShowIf]};
    }

    return blockChildrenShowIf;
}

/**
 * Создает начальное состояние для экземпляра Executor.
 * Использует QuestionModel для вычисления состояния узлов.
 * @param {object} config - исходные данные, которые необходимо обработать
 * @return {object} начальное состояние
 */
function prepareState(config) {
    if (!config || !config.questions || !Object.keys(config.questions).length) {
        return null;
    }

    let state = {
        query: {},
        questions: {}
    };

    ['questions', 'query'].forEach(type => {
        const nodes = config[type] || {};

        Object.keys(nodes).reduce((state, id) => {
            const nodeValue = nodes[id];
            state[type][id] = setState(type, nodeValue);
            return state;
        }, state);
    });

    return state;
}

/**
 * Функция хелпер для prepareState.
 * @param {string} type - тип узла
 * @param {string|number|object} nodeValue - значение узла
 * @returns {object} состояние узла
 */
function setState(type, nodeValue) {
    switch (type) {
        case 'questions':
            return {
                _value: _getValue(nodeValue),
                other: {_value: _getOtherValue(nodeValue)}
            };
        case 'query':
            return {
                _value: nodeValue || null
            };
        default:
            return null;
    }
}

/**
 * Создает массивмассив элементов, каждый из которых определяет условия показа соответствующей группы/вопроса,
 * для экземпляра Executor из данных, пришедших с сервера.
 * @param {object} config - исходные данные, которые необходимо обработать
 * @return {array} массив узлов
 */
function prepareNodes(config, errCb) {
    const nodes = [];
    const {groups = {}, questions = {}, widgets = {}, conditions = {}} = config;

    Object.keys(groups).forEach(groupId => {
        const group = groups[groupId];

        _append(nodes, {
            id: Number(groupId),
            type: 'group'
        }, _combineShowIf({group, questions, widgets}));

        if (group.isScreening) {
            _append(nodes, {
                id: Number(groupId),
                type: 'screening'
            }, group.condition);
        }
    });

    _prepareConditions(conditions, nodes);
    _prepareQuestions(questions, nodes);
    _prepareWidgets(widgets, nodes);
    _validateNodes({config, nodes, errCb});

    return nodes;
}

function _validateNodes({config, nodes, errCb}) {
    const errors = [];
    let i = 0;

    while (nodes.length > i) {
        const {type, expr, id} = nodes[i];
        const extraText = ` in ${type} -> ${id}`;
        const {isValid, error} = validation(expr, config, extraText);

        if (!isValid) {
            errors.push(error.message);
            nodes.splice(i, 1);
            /*
            * После splice элементы в масси сдвинутся и если i увеличить на единицу,
            * то будет пропущен один элемент массива, поэтому просто continue
            * */
            continue;
        }

        i++;
    }

    if (errors.length) {
        errCb(new Error(errors.reduce((prev, curr) => `${prev}\n${curr}`, errors.shift())));
    }
}

/**
 * Добавляет массив элементов (каждый из которых определяет условия показа соответствующей группы/вопроса),
 * полученный из данных вопросов, в массив узлов.
 * @param {object} questions - вопросы, которые необходимо обработать
 * @param {array} nodes - массив узлов
 */
function _prepareQuestions(questions, nodes) {
    Object.keys(questions).forEach(id => {
        id = Number(id);
        const question = questions[id];
        _append(nodes, {
            id,
            type: 'question'
        }, question.showIf);

        if (question.options) {
            question.options.forEach(o => _append(nodes, {
                questionId: id,
                type: 'option',
                id: o.id
            }, o.showIf));
        }
    });
}

function _prepareWidgets(widgets, nodes) {
    Object.keys(widgets).forEach(id => {
        id = Number(id);
        const widget = widgets[id];
        _append(nodes, {
            id,
            type: 'widget'
        }, widget.showIf);
    });
}

function _prepareConditions(conditions, nodes) {
    Object.keys(conditions).forEach(id => {
        _append(nodes, {
            id,
            type: 'condition'
        }, conditions[id]);
    });
}

module.exports = {prepareNodes, prepareState};
